import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Store } from '@ngxs/store';
import { Subject, takeUntil } from 'rxjs';
import { MarkMessageAsRead } from '../state/comments.actions';
import { CommentsApiService } from '../comments-api.service';
import { Comment, CommentSectionItem, MessageEntityType } from '../comments.model';
import { CommentsService } from '../comments.service';
import { CommentsSelectors } from '../state/comments.selectors';
import { DATA_SELECTOR_INJECTION_TOKEN, Logger, SharedModule } from '@aiwyn/shared';
import { AuthService } from '@aiwyn/auth';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatProgressBar } from '@angular/material/progress-bar';
import { tap } from 'rxjs/operators';
import { isNil } from 'lodash';
import { hasUnreadMessages } from '../comments.utils';

@Component({
  selector: 'app-comments-section',
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, MatButtonModule, SharedModule, MatProgressBar, MatProgressSpinner],
  providers: [{ provide: DATA_SELECTOR_INJECTION_TOKEN, useValue: 'comments-section' }],
  templateUrl: './comments-section.component.html',
  styleUrl: './comments-section.component.scss',
  host: {
    class: 'comments-section-host',
    '[style.display]': '"flex"',
    '[style.flex-direction]': '"column"',
    '[style.flex]': '"1"',
    '[style.min-height]': '"0"',
    '[style.max-width]': '"100%"',
    '[style.height]': '"100%"',
    '[style.overflow]': '"hidden"',
  },
})
export class CommentsSectionComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  @ViewChild('commentsList', { read: ElementRef }) commentsListRef: ElementRef<HTMLDivElement>;
  @ViewChild('threadView', { read: ElementRef }) threadViewRef: ElementRef<HTMLDivElement>;

  /**
   * The IDs of the entities (e.g., client request item IDs)
   * Required to load comments from the API
   */
  entityIds = input.required<number[]>();

  /**
   * The type of entity being commented on
   * Defaults to CLIENT_REQUEST_ITEM
   */
  entityType = input<MessageEntityType>(MessageEntityType.CLIENT_REQUEST_ITEM);

  hideCommentInput = input<boolean | null>(null);
  isActivityLoading = input<boolean>(false);

  // Services
  private readonly authService = inject(AuthService);
  private readonly commentsApiService = inject(CommentsApiService);
  private readonly logger = inject(Logger);

  // Intersection Observer for tracking message visibility
  private intersectionObserver: IntersectionObserver | null = null;
  private visibilityTimers = new Map<string, number>();

  // Signal for comments from store
  comments = signal<CommentSectionItem[]>([]);
  isLoadingComments = signal<boolean>(false);

  // UI state signals
  viewingThreadId = signal<string | null>(null);

  // Form control for message input
  messageControl = new FormControl<string>('');

  // Computed signals
  isViewingThread = computed(() => this.viewingThreadId() !== null);
  showCommentInput = computed(() => {
    const ids = this.entityIds() ?? [];
    const hideCommentInput = this.hideCommentInput();
    return isNil(hideCommentInput) ? this.viewingThreadId() || ids.length === 1 : !hideCommentInput;
  });
  shouldMaskComments = computed(() => (this.threadComment()?.replies ?? this.displayComments()).some((comment) => comment['isMasked']));

  // Current user computed signals
  currentUser = toSignal(this.authService.getUser());

  threadComment = computed(() => {
    const threadId = this.viewingThreadId();
    if (!threadId) {
      return null;
    }
    return this.comments().find((c) => c.id === threadId) || null;
  });

  displayComments = computed(() => {
    return this.isViewingThread() ? [] : this.comments();
  });

  inputPlaceholder = computed(() => {
    return this.threadComment()
      ? 'Replies will not be submitted as responses to requests.'
      : 'Add comments to share context or ask questions.\nThey won’t be submitted as responses to requests.';
  });

  private destroy$ = new Subject<void>();
  private shouldScrollToBottom = false;
  private previousCommentsLength = 0;

  constructor(
    private commentsService: CommentsService,
    private store: Store,
  ) {
    // Effect to load comments when entityIds or entityType changes
    effect(() => {
      const ids = this.entityIds();
      const type = this.entityType();
      if (ids && ids.length > 0) {
        this.commentsService.loadComments(ids, type);
      }
    });
  }

  ngOnInit(): void {
    // Subscribe to comments from store for all entities
    this.store
      .select(CommentsSelectors.getCommentsPlusActivity)
      .pipe(
        tap((state) => {
          const entityIds = this.entityIds();
          const entityType = this.entityType();
          const comments = CommentsSelectors.commentsByEntity(state, entityIds, entityType);

          // Check if new comment was added
          if (comments.length > this.previousCommentsLength) {
            this.shouldScrollToBottom = true;
          }
          this.previousCommentsLength = comments.length;
          this.comments.set(comments);
          this.isLoadingComments.set(state.loading);
          this.setInputCommentDisabledState(state.loading);

          const selectedParentId = this.viewingThreadId();
          if (!comments.find((comment) => comment.id === selectedParentId)) {
            this.viewingThreadId.set(null);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }

    // Re-observe messages after view changes
    this.observeMessages();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    // Clear all pending timers
    this.visibilityTimers.forEach((timerId) => clearTimeout(timerId));
    this.visibilityTimers.clear();
  }

  handleKeyDown(event: KeyboardEvent): void {
    // Only send on Enter without Shift (Shift+Enter allows multiline)
    if (!event.shiftKey) {
      event.preventDefault();
      this.handleSendMessage();
    }
  }

  handleMessageClicked(comment: Comment): void {
    this.markMessageAsViewed(comment?.id);
  }

  handleSendMessage(): void {
    const message = this.messageControl.value;
    if (!message?.trim()) {
      return;
    }
    if (!this.showCommentInput()) {
      return;
    }

    const parentId = this.viewingThreadId();

    // Determine which entityId to use
    let targetEntityId: number;
    if (parentId) {
      // For replies, get entityId from the parent comment
      const parentComment = this.comments().find((c) => c.id === parentId);
      targetEntityId = parentComment?.entityId;
      if (!targetEntityId) {
        return;
      }
    } else {
      const entityIds = this.entityIds();
      if (entityIds.length > 1) {
        return;
      }
      // For new top-level comments, use the first entityId
      targetEntityId = entityIds[0];
    }

    this.commentsService.postComment({
      text: message.trim(),
      entityId: targetEntityId,
      entityType: this.entityType(),
      ...(parentId && { parentId }),
    });

    // Clear input
    this.messageControl.reset();
  }

  startReply(commentId: string): void {
    // Open thread view (same as viewThread)
    this.viewingThreadId.set(commentId);
  }

  viewThread(commentId: string): void {
    this.viewingThreadId.set(commentId);
  }

  closeThread(): void {
    this.viewingThreadId.set(null);
  }

  getTimeAgo(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    }
    if (diffMins < 60) {
      return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    }
    if (diffHours < 24) {
      return `Today ${this.formatTime(date)}`;
    }
    if (diffDays === 1) {
      return `Yesterday ${this.formatTime(date)}`;
    }
    if (diffDays < 7) {
      return `${diffDays} days ago`;
    }

    return this.formatDate(date);
  }

  private formatTime(date: Date): string {
    return new Date(date)
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .toLowerCase();
  }

  private formatDate(date: Date): string {
    return (
      new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }) +
      ' ' +
      this.formatTime(date)
    );
  }

  getRepliesCount(comment: Comment): number {
    return comment.replies?.length || 0;
  }

  hasReplies(comment: Comment): boolean {
    return (comment.replies?.length || 0) > 0;
  }

  setInputCommentDisabledState(isLoading: boolean): void {
    if (isLoading) {
      if (!this.messageControl.disabled) {
        this.messageControl.disable({ onlySelf: true, emitEvent: false });
      }
    } else {
      if (this.messageControl.disabled) {
        this.messageControl.enable({ onlySelf: true, emitEvent: false });
      }
    }
  }

  private scrollToBottom(): void {
    try {
      // Scroll the appropriate container based on current view
      const scrollContainer = this.isViewingThread() ? this.threadViewRef?.nativeElement : this.commentsListRef?.nativeElement;

      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    } catch (err) {
      this.logger.error('Error scrolling to bottom:', err);
    }
  }

  /**
   * Sets up the Intersection Observer to track message visibility
   */
  private setupIntersectionObserver(): void {
    const options: IntersectionObserverInit = {
      root: null, // Use viewport as root
      rootMargin: '0px',
      threshold: 0.5, // At least 50% of the message must be visible
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const messageId = (entry.target as HTMLElement).getAttribute('data-message-id');
        if (!messageId) {
          return;
        }

        if (entry.isIntersecting) {
          // Message became visible - start 3 second timer
          this.startVisibilityTimer(messageId);
        } else {
          // Message is no longer visible - cancel timer
          this.cancelVisibilityTimer(messageId);
        }
      });
    }, options);
  }

  /**
   * Observes all message elements in the current view
   */
  private observeMessages(): void {
    if (!this.intersectionObserver) {
      return;
    }

    const container = this.isViewingThread() ? this.threadViewRef?.nativeElement : this.commentsListRef?.nativeElement;

    if (!container) {
      return;
    }

    // Find all message elements
    const messageElements = container.querySelectorAll('[data-message-id]');

    messageElements.forEach((element) => {
      // Only observe if not already being observed
      this.intersectionObserver?.observe(element);
    });
  }

  /**
   * Starts a 3-second timer for a message
   * After 3 seconds, marks the message as viewed
   */
  private startVisibilityTimer(messageId: string): void {
    // Don't start timer if already exists
    if (this.visibilityTimers.has(messageId)) {
      return;
    }

    // Check if message exists and is itself unread
    const comment = this.findCommentById(messageId);
    if (!comment || !comment.isUnread) {
      return;
    }

    const timerId = window.setTimeout(() => {
      this.markMessageAsViewed(messageId);
      this.visibilityTimers.delete(messageId);
    }, 3000); // 3 seconds

    this.visibilityTimers.set(messageId, timerId);
  }

  /**
   * Cancels the visibility timer for a message
   */
  private cancelVisibilityTimer(messageId: string): void {
    const timerId = this.visibilityTimers.get(messageId);
    if (timerId) {
      clearTimeout(timerId);
      this.visibilityTimers.delete(messageId);
    }
  }

  /**
   * Marks a message as viewed via API and updates state
   * Guard: Only calls backend if message or any of its replies are unread
   */
  private markMessageAsViewed(messageId: string): void {
    if (!messageId) {
      return;
    }

    const contactId = this.currentUser().getContactId();
    if (!contactId) {
      this.logger.error('Cannot mark message as viewed: staffId is null');
      return;
    }

    // Find the comment to check if it or its replies are unread
    const comment = this.findCommentById(messageId);
    if (!comment) {
      this.logger.error('Cannot mark message as viewed: comment not found');
      return;
    }

    // Guard: Don't call backend if both the message and all its replies are already read
    if (!comment.isUnread && !this.hasAnyUnreadReplies(comment)) {
      this.logger.debug('Skipping backend call: message and all replies already read', messageId);
      return;
    }

    this.commentsApiService
      .markMessageAsViewed(parseInt(messageId, 10), contactId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Dispatch action to update state with the comment's entityId
          this.store.dispatch(new MarkMessageAsRead(messageId, comment.entityId, this.entityType()));
        },
        error: (error) => {
          this.logger.error('Error marking message as viewed:', error);
        },
      });
  }

  /**
   * Checks if a comment has any unread replies
   */
  private hasAnyUnreadReplies(comment: Comment): boolean {
    if (!comment.replies || comment.replies.length === 0) {
      return false;
    }
    return comment.replies.some((reply) => hasUnreadMessages(reply));
  }

  /**
   * Finds a comment by ID (recursively searches through replies)
   */
  private findCommentById(messageId: string): Comment | null {
    const searchInComments = (comments: Comment[]): Comment | null => {
      for (const comment of comments) {
        if (comment.id === messageId) {
          return comment;
        }
        if (comment.replies && comment.replies.length > 0) {
          const found = searchInComments(comment.replies);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    return searchInComments(this.comments());
  }
}
